import { UserRole, AgentType, UserPermissions } from '../types/auth';
import { isAdminDomain } from '../utils/domainUtils';

export class PermissionService {
  private static instance: PermissionService;

  private constructor() {}

  public static getInstance(): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService();
    }
    return PermissionService.instance;
  }

  /**
   * Detect user role based on current domain
   */
  detectUserRole(): UserRole {
    // Check if it's the admin subdomain
    if (isAdminDomain()) {
      return 'super_admin';
    }
    
    // Default to admin (agent) role for main domain
    return 'admin';
  }

  /**
   * Get user permissions based on role and agent type
   */
  getUserPermissions(role: UserRole, agentType?: AgentType): UserPermissions {
    switch (role) {
      case 'super_admin':
        return {
          canManageLedger: true,
          canModifyClients: true,
          canAddClients: true,
          canViewAllClients: true,
          canManageUsers: true
        };
      
      case 'admin':
        return {
          canManageLedger: false, // Ordinary admins cannot access ledger
          canModifyClients: false, // Cannot modify existing clients
          canAddClients: true,     // Can add new clients
          canViewAllClients: false, // Can only view their assigned clients
          canManageUsers: false
        };
      
      default:
        return {
          canManageLedger: false,
          canModifyClients: false,
          canAddClients: false,
          canViewAllClients: false,
          canManageUsers: false
        };
    }
  }

  /**
   * Check if a user can access a specific client based on their agent type
   */
  canAccessClient(userRole: UserRole, userAgentType: AgentType | undefined, clientAgentType: AgentType): boolean {
    if (userRole === 'super_admin') {
      return true; // Super admin can access all clients
    }
    
    if (userRole === 'admin' && userAgentType) {
      return userAgentType === clientAgentType; // Admin can only access their assigned clients
    }
    
    return false;
  }

  /**
   * Check if a user can perform a specific action
   */
  canPerformAction(
    userRole: UserRole, 
    userAgentType: AgentType | undefined, 
    action: keyof UserPermissions,
    clientAgentType?: AgentType
  ): boolean {
    const permissions = this.getUserPermissions(userRole, userAgentType);
    
    if (!permissions[action]) {
      return false;
    }
    
    // For client-specific actions, check if user can access the client
    if (action === 'canModifyClients' && clientAgentType) {
      return this.canAccessClient(userRole, userAgentType, clientAgentType);
    }
    
    return true;
  }

  /**
   * Get the collection center name based on agent type
   */
  getCollectionCenter(agentType: AgentType): string {
    switch (agentType) {
      case 'NA':
        return 'Nukyat';
      case 'PA':
        return 'Parotion';
      case 'TMA':
        return 'T Moita';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get agent type from collection center name
   */
  getAgentTypeFromCollectionCenter(collectionCenter: string): AgentType | null {
    switch (collectionCenter.toLowerCase()) {
      case 'nukyat':
        return 'NA';
      case 'parotion':
        return 'PA';
      case 't moita':
      case 'tmoita':
        return 'TMA';
      default:
        return null;
    }
  }
}

export const permissionService = PermissionService.getInstance();
